import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/handleError";
import {companyAPI} from "../api";
import {
    ICompaniesBalance,
    ICompany,
    IGetBalanceHistoryParams,
    IUpdateCompanyTariffParams
} from "../../../models/iCompanies";
import {RootState} from "../../../store";
import {filesAPI} from "../../files/api";
import {setModalMessage} from "../../messages/model/slice";
import axios from "axios";
import {ICreatePaymentRequest, ICreatePaymentResponse} from "../../../models/iPayment";

export const fetchGetAllCompanies = createAsyncThunk(
    "companies/fet_all",
    async (_, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.getAll();
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchCheckCompanyById = createAsyncThunk(
    "companies/check_company_by_id",
    async (company_id: string, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.checkById(company_id);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchUpdateCompany = createAsyncThunk(
    "companies/update",
    async (company: ICompany, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.update(company);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    });

export const fetchDeleteCompany = createAsyncThunk(
    "companies/remove",
    async (companyId: string, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.delete(companyId);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchUpdateCompanyBalance = createAsyncThunk(
    "companies/balance_update",
    async (balance: ICompaniesBalance, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.updateBalance(balance);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    });

export const fetchGetBalanceHistory = createAsyncThunk(
    'companies/balance_history',
    async (
        params: IGetBalanceHistoryParams,
        {rejectWithValue, dispatch, signal},
    ) => {
        try {
            return await companyAPI.getBalanceHistory(params, signal);
        } catch (e) {
            // ✅ axios.isCancel ловит и CanceledError и старый Cancel
            // ✅ signal.aborted — RTK abort() через AbortController
            if (axios.isCancel(e) || signal.aborted) {
                return rejectWithValue('cancelled');
            }
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

interface IUploadPhoto {
    company: ICompany;
    file: File;
}

export const fetchUploadCompanyPhoto = createAsyncThunk<ICompany, IUploadPhoto, { state: RootState }>(
    "company/upload_photo",
    async (uploadData: IUploadPhoto, {rejectWithValue, dispatch}) => {
        try {
            const {file, company} = uploadData;
            if (company.logo_path) {
                await filesAPI.delete(company.logo_path);
            }
            const res = await filesAPI.upload(file);
            if (!res) return;
            const updatedCompany = {...company, logo_path: res};
            return dispatch(fetchUpdateCompany(updatedCompany)).unwrap();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchRemoveCompanyPhoto = createAsyncThunk(
    "company/remove_photo",
    async (company: ICompany, {rejectWithValue, dispatch}) => {
        try {
            await filesAPI.delete(company.logo_path);
            const updatedCompany = {
                ...company,
                logo_path: "",
            };
            return await dispatch(fetchUpdateCompany(updatedCompany)).unwrap();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUpdateCompanyTariff = createAsyncThunk(
    "companies/update_tariff",
    async (params: IUpdateCompanyTariffParams, { rejectWithValue, dispatch }) => {
        try {
            return await companyAPI.updateTariff(params);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchGetBalanceInfo = createAsyncThunk(
    "companies/balance_info",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            return await companyAPI.getBalanceInfo();
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchCreatePayment = createAsyncThunk<
    ICreatePaymentResponse,
    ICreatePaymentRequest
>(
    "companies/create_payment",
    async (data, {rejectWithValue, dispatch}) => {
        try {
            return await companyAPI.createPayment(data);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);


